import { makeObservable } from 'mobx';

import type { RenderedServiceData } from 'chaika-frontend-types/frontend-cms';
import { RenderedServiceStatus } from 'chaika-enums';

import { GroupedList, GroupedListParams } from '@store/abstract/GroupedList';
import isEmpty from 'lodash/isEmpty';

interface FilterParams {
    ids?: string[];
    statuses?: RenderedServiceStatus[];
    serviceIds?: string[];
}

export class RenderedServiceList extends GroupedList<
    GroupedListParams<RenderedServiceData>,
    RenderedServiceData,
    FilterParams
> {
    constructor(params: RenderedServiceData[]) {
        super();
        makeObservable(this);

        this.filterParams = {
            statuses: [],
        };

        this.set(params || []);
    }

    public serialize(): RenderedServiceData[] {
        return this.values;
    }

    protected toStringGroupParams({
        serviceId,
        entryId,
        plans,
        status,
        toothNumber,
        isFinedService,
        markingCode,
        isMandatory,
    }:
        | (GroupedListParams<RenderedServiceData> & { ids: string[]; count: number; groupId: string })
        | RenderedServiceData): string {
        return `${serviceId}${entryId}${plans.map(({ id }) => id).join(',')}${status}:${toothNumber}:${isFinedService}:${markingCode}:${Boolean(isMandatory)}`;
    }

    protected create(params: GroupedListParams<RenderedServiceData>): GroupedListParams<RenderedServiceData> {
        return params;
    }

    protected filterValue(
        renderedService: GroupedListParams<RenderedServiceData>,
        filterParams: FilterParams,
    ): boolean {
        return (
            this.filterFieldValueByArray(renderedService, filterParams.ids, 'id') &&
            (!isEmpty(filterParams.statuses)
                ? this.filterFieldValueByArray(renderedService, filterParams.statuses, 'status')
                : true) &&
            (!isEmpty(filterParams.serviceIds)
                ? this.filterFieldValueByArray(renderedService, filterParams.serviceIds, 'serviceId')
                : true)
        );
    }

    protected mergeCreateParams(params: RenderedServiceData[]): RenderedServiceData {
        const paymentData = params.reduce(
            (acc, renderedService) => {
                return {
                    amountToPay: acc.amountToPay + renderedService.amountToPay,
                    totalCost: acc.totalCost + renderedService.totalCost,
                    insuranceAmount: acc.insuranceAmount + (renderedService.insuranceAmount || 0),
                };
            },
            { amountToPay: 0, totalCost: 0, insuranceAmount: 0 },
        );

        return {
            ...params[0],
            ...paymentData,
        };
    }
}
